import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ContactLogForm } from './ContactLogForm'
import { Edit, Trash2 } from 'lucide-react'
import { formatDateTime, formatDate } from '@/lib/format'

interface ContactLogDetailModalProps {
  contactLog: any
  isOpen: boolean
  onClose: () => void
  onUpdate: (id: string, updates: any) => Promise<void>
  onDelete: (id: string) => Promise<void>
  updating?: boolean
  deleting?: boolean
}

export function ContactLogDetailModal({
  contactLog,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  updating,
  deleting,
}: ContactLogDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)

  const handleUpdate = async (data: any) => {
    await onUpdate(contactLog.id, data)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete this contact log entry? This action cannot be undone.`)) {
      return
    }

    await onDelete(contactLog.id)
    onClose()
  }

  if (isEditing) {
    return (
      <Modal isOpen={isOpen} onClose={() => setIsEditing(false)} title="Edit Contact Log" size="lg">
        <ContactLogForm
          initialData={contactLog}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          loading={updating}
        />
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Contact Log Details" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Contact Date</p>
            <p className="text-sm text-gray-900">{formatDateTime(contactLog.contact_date)}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Contact Method</p>
            <Badge>{contactLog.contact_method}</Badge>
          </div>
        </div>

        {contactLog.person_contacted && (
          <div>
            <p className="text-sm font-medium text-gray-500">Person Contacted</p>
            <p className="text-sm text-gray-900">{contactLog.person_contacted}</p>
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-gray-500">Summary</p>
          <p className="text-sm text-gray-900">{contactLog.summary}</p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500">Outcome</p>
          <p className="text-sm text-gray-900">{contactLog.outcome}</p>
        </div>

        {contactLog.next_action && (
          <div>
            <p className="text-sm font-medium text-gray-500">Next Action</p>
            <p className="text-sm text-gray-900">{contactLog.next_action}</p>
          </div>
        )}

        {contactLog.followup_date && (
          <div>
            <p className="text-sm font-medium text-gray-500">Follow-Up Date</p>
            <p className="text-sm text-gray-900">{formatDate(contactLog.followup_date)}</p>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t">
          <Button variant="danger" onClick={handleDelete} loading={deleting}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <div className="flex space-x-3">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
